import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import InfoTable from "../../../../components/InfoTable";
import { COLORS } from "../../../../utils/colors";
import { api } from "../../../../utils/api";
import {
  XeroContactMismatchAlert,
  XeroContactResolveDialog,
} from "../../../../components/XeroContactMismatch";

const MerchantInfo = ({
  merchant,
  setMerchant,
  setModalTitle,
  setModalContent,
  handleDisplayModal,
}) => {
  const isAdmin = sessionStorage.getItem("selectedPortal") == "Administrator";
  const [xeroCheck, setXeroCheck] = useState(null);
  const [xeroCheckLoading, setXeroCheckLoading] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);

  const fetchXeroCheck = async () => {
    if (!merchant?._id || !isAdmin) return;
    if (merchant.skipXeroContactCheck) {
      setXeroCheck({
        matches: true,
        reason: "SKIP_CHECK",
        skipped: true,
        mismatches: [],
      });
      return;
    }
    setXeroCheckLoading(true);
    try {
      const response = await api(
        `v1/merchant/${merchant._id}/xero-contact?refresh=1`,
        "GET"
      );
      if (response?.data?.success) {
        setXeroCheck(response.data.data?.check || null);
      } else {
        setXeroCheck({
          matches: false,
          reason: "XERO_UNAVAILABLE",
          message: response?.data?.message || "Could not check the Xero contact for this merchant.",
          mismatches: [],
        });
      }
    } catch (error) {
      setXeroCheck({
        matches: false,
        reason: "XERO_UNAVAILABLE",
        message: error?.response?.data?.message || "Could not check the Xero contact for this merchant.",
        mismatches: [],
      });
    } finally {
      setXeroCheckLoading(false);
    }
  };

  useEffect(() => {
    if (merchant?.skipXeroContactCheck) {
      setXeroCheck({
        matches: true,
        reason: "SKIP_CHECK",
        skipped: true,
        mismatches: [],
      });
    } else {
      setXeroCheck(null);
    }
  }, [merchant?._id, merchant?.skipXeroContactCheck]);

  return (
    <div className="mt-4 flex flex-column gap-2">
      <h3 className="text-lg font-bold">Billing Details</h3>
      {isAdmin && (
        <>
          <XeroContactMismatchAlert
            check={xeroCheck}
            loading={xeroCheckLoading}
            merchantId={merchant?._id}
            onFix={() => setResolveOpen(true)}
            onAccepted={(nextCheck, updatedMerchant) => {
              setXeroCheck(nextCheck || null);
              if (updatedMerchant && typeof setMerchant === "function") {
                setMerchant(updatedMerchant);
              }
            }}
          />
          <XeroContactResolveDialog
            open={resolveOpen}
            onClose={() => setResolveOpen(false)}
            merchantId={merchant?._id}
            check={xeroCheck}
            onResolved={(nextCheck, updatedMerchant) => {
              setXeroCheck(nextCheck || null);
              if (updatedMerchant && typeof setMerchant === "function") {
                setMerchant(updatedMerchant);
              }
              if (nextCheck?.matches) setResolveOpen(false);
              else fetchXeroCheck();
            }}
          />
        </>
      )}

      <InfoTable data={merchant} />
      {sessionStorage.getItem("selectedPortal") == "Administrator" && (
        <div className="flex gap-1 flex-wrap">
          {!merchant?.skipXeroContactCheck && (
            <Button
              variant="outlined"
              disabled={xeroCheckLoading}
              onClick={() => fetchXeroCheck()}
            >
              {xeroCheckLoading ? "Checking Xero contact…" : "Check Xero contact"}
            </Button>
          )}
          <Button
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            variant="contained"
            onClick={() => {
              setModalTitle("Edit Merchant Details");
              setModalContent("editMerchant");
              handleDisplayModal(true);
            }}
          >
            Edit Merchant Details
          </Button>
          <Button
            className="self-start bg-red-600"
            variant="contained"
            color="error"
            onClick={() => {
              setModalTitle("Delete Merchant Account?");
              setModalContent("deleteMerchant");
              handleDisplayModal(true);
            }}
          >
            Delete Merchant
          </Button>
        </div>
      )}
    </div>
  );
};

export default MerchantInfo;
